import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import type { OpenTab } from "@/context/OpenTabsContext";
import { getObjectActionDefinition } from "@/object-actions/catalog";
import { getActionIcon, getActionLabel } from "@/object-actions/utils";
import { cn } from "@/utils";
import { ChevronRight, Minus, Pencil, Trash2, X, XCircle } from "lucide-react";

type WorkspaceTabContextMenuProps = {
  x: number;
  y: number;
  tabKind: OpenTab["kind"];
  isFavorite: boolean;
  hasOtherTabs: boolean;
  hasTabsToRight: boolean;
  onClose: () => void;
  onRename: () => void;
  /** Omitted for tab kinds the favorite toggle doesn't cover. */
  onToggleFavorite?: () => void;
  onCloseTab: () => void;
  onCloseOthers: () => void;
  onCloseRight: () => void;
  onCloseAll: () => void;
  /** Archive the tab's resource; omitted when the user can't archive it. */
  onDelete?: () => void;
};

/**
 * Right-click menu for workspace tabs. Close actions use muted X-family icons
 * (closing a tab never archives anything); the destructive Archive row is
 * separated and rendered in the danger treatment per the design system.
 */
export function WorkspaceTabContextMenu({
  x,
  y,
  tabKind,
  isFavorite,
  hasOtherTabs,
  hasTabsToRight,
  onClose,
  onRename,
  onToggleFavorite,
  onCloseTab,
  onCloseOthers,
  onCloseRight,
  onCloseAll,
  onDelete,
}: WorkspaceTabContextMenuProps) {
  const favoriteDefinition = getObjectActionDefinition(
    isFavorite ? "unstar" : "star",
  );
  const labelCtx = { kind: { type: tabKind } };
  const FavoriteIcon = getActionIcon(favoriteDefinition, labelCtx);

  return (
    <Popover
      defaultOpen
      modal={false}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <PopoverAnchor asChild>
        <div
          aria-hidden
          className="pointer-events-none fixed size-px shrink-0"
          style={{ left: x, top: y }}
        />
      </PopoverAnchor>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={4}
        collisionPadding={8}
        className="w-48 p-0"
        onEscapeKeyDown={(e) => {
          e.stopPropagation();
          onClose();
        }}
        onInteractOutside={() => {
          onClose();
        }}
      >
        <Command>
          <CommandList className="max-h-none overflow-visible">
            <CommandGroup>
              <CommandItem
                onSelect={() => {
                  onRename();
                }}
              >
                <Pencil />
                Rename
              </CommandItem>
              {onToggleFavorite && (
                <CommandItem
                  onSelect={() => {
                    onToggleFavorite();
                  }}
                >
                  <FavoriteIcon
                    className={cn(
                      isFavorite && "fill-amber-500 text-amber-500",
                    )}
                  />
                  {getActionLabel(favoriteDefinition, labelCtx)}
                </CommandItem>
              )}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup>
              <CommandItem
                onSelect={() => {
                  onCloseTab();
                }}
              >
                <X />
                Close
              </CommandItem>
              <CommandItem
                disabled={!hasOtherTabs}
                onSelect={() => {
                  if (hasOtherTabs) onCloseOthers();
                }}
              >
                <Minus />
                Close others
              </CommandItem>
              <CommandItem
                disabled={!hasTabsToRight}
                onSelect={() => {
                  if (hasTabsToRight) onCloseRight();
                }}
              >
                <ChevronRight />
                Close to the right
              </CommandItem>
              <CommandItem
                onSelect={() => {
                  onCloseAll();
                }}
              >
                <XCircle />
                Close all
              </CommandItem>
            </CommandGroup>
            {onDelete && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    className={cn(
                      "text-destructive data-[selected=true]:text-destructive data-[selected=true]:bg-destructive/15",
                    )}
                    onSelect={() => {
                      onDelete();
                    }}
                  >
                    <Trash2 />
                    Archive
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
